import { Button } from '@/app/components/button';
import { Divider } from '@/app/components/divider';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { Image } from '@/app/components/media/image';
import { Link } from '@/app/components/navigation/link';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { formatPrice, getPartPrefix } from '@/app/utils/format';
import {
  type ComponentServicesGenuinePart,
  Enum_Componentservicesgenuinepart_Genuineparttype,
  type PartItem as PartItemType,
} from '@/types/tis-cms/types';

export interface PartItemProps {
  className?: string;
  index: number;
  partImage?: PartItemType['partImage'];
  headlineTitle?: PartItemType['headlineTitle'];
  genuineParts?: PartItemType['genuineParts'];
  url?: PartItemType['url'];
  testId?: string;
  dataTrack?: string;
}

const PRICE_STRINGS = {
  specialPriceLabel: 'พิเศษเริ่ม',
  regularPriceLabel: 'ปกติ',
  startingLabel: 'เริ่ม',
};

const PriceRow = ({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId: string;
}) => {
  return (
    <div className="flex flex-col justify-between gap-x-3 sm:flex-row">
      {!!label && (
        <p
          className="text-body-2-fixed where-span:text-inline-wrap"
          dangerouslySetInnerHTML={{ __html: label }}
        />
      )}
      {!!value && (
        <p
          className="text-body-2-fixed font-noto-bold where-span:text-inline-wrap"
          dangerouslySetInnerHTML={{ __html: value }}
          {...createDataAttributes({ test: testId })}
        />
      )}
    </div>
  );
};

const PriceDetails = ({
  sub,
  index,
  testId,
}: {
  sub: ComponentServicesGenuinePart | null;
  index: number;
  testId: string;
}) => {
  if (!sub) return null;

  const {
    genuinePartType,
    price,
    specialPrice,
    showStartPricePrefix,
    title,
    unit,
  } = sub;

  if (specialPrice) {
    return (
      <>
        <PriceRow
          label={PRICE_STRINGS.specialPriceLabel}
          value={`${formatPrice(specialPrice, 'none')} ${unit ?? ''}`}
          testId={`${testId}-specialprice-${index}`}
        />
        {!!price && (
          <PriceRow
            label={PRICE_STRINGS.regularPriceLabel}
            value={`${formatPrice(price, 'none')} ${unit ?? ''}`}
            testId={`${testId}-regularprice-${index}`}
          />
        )}
      </>
    );
  }

  return (
    <>
      {!!(
        title ||
        genuinePartType !==
          Enum_Componentservicesgenuinepart_Genuineparttype.Part
      ) ? (
        <PriceRow
          label={getPartPrefix(sub)}
          value={`${
            showStartPricePrefix ? PRICE_STRINGS.startingLabel : ''
          } ${formatPrice(price, 'none')} ${unit ?? ''}`}
          testId={`${testId}-price-${index}`}
        />
      ) : (
        <PriceRow
          label={!!showStartPricePrefix ? PRICE_STRINGS.startingLabel : ''}
          value={`${formatPrice(price, 'none')} ${unit ?? ''}`}
          testId={`${testId}-price-${index}`}
        />
      )}
    </>
  );
};

export const PartItem = ({
  className,
  index,
  headlineTitle,
  partImage,
  genuineParts = [],
  url,
  testId = 'related-parts',
  dataTrack = 'related-parts',
}: PartItemProps) => {
  if (!genuineParts) return null;

  return (
    <Link
      href={`${url}`}
      className={cn(
        'group relative flex h-full flex-col gap-y-3 md:gap-y-4',
        className
      )}
      {...createDataAttributes({ test: `${testId}-item-link-${index}` })}
      {...createDataGA({
        click: true,
        eventName: dataTrack,
        group: dataTrack,
        url: `${url}`,
        text: headlineTitle,
      })}
    >
      {!!partImage?.url && (
        <div className="aspect-4-5 ease-bezier transition-bg-color relative w-full rounded-sm bg-gray-300 duration-500 group-hover:bg-gray-600 focus-within:bg-gray-600 md:rounded-md">
          <Image
            src={partImage.url}
            alt={headlineTitle || ''}
            width={138}
            height={160}
            layout="ratio"
            aspectRatio="aspect-4-5"
            objectFit="contain"
            sizes="(max-width: 479px) 280px, 320px"
          />
        </div>
      )}
      <div>
        {!!headlineTitle && (
          <div className="flex items-center justify-between">
            <h4
              className="text-body-2 font-noto-bold"
              {...createDataAttributes({
                test: `${testId}-item-link-${index}`,
              })}
            >
              {headlineTitle}
            </h4>
            <Button
              color="white"
              size="2xs"
              iconBefore={<IconArrowForward />}
              className="flex-none self-start bg-transparent group-hover:bg-gray-600 group-hover:text-white focus-within:bg-gray-600 focus-within:text-white"
            />
          </div>
        )}
        {genuineParts.length > 0 && (
          <div className="mt-1 grid">
            {genuineParts.map((sub, i) => (
              <PriceDetails
                key={i}
                sub={sub}
                index={i}
                testId={`${testId}-item-genuine-parts-${i}`}
              />
            ))}
          </div>
        )}
      </div>
      <Divider className="mt-auto mb-0" />
    </Link>
  );
};
